import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  checkAddPermission,
  checkDeletePermission,
  checkEditPermission,
  handleExportFile,
  handleImportClickFile,
  handleUpdateAllCheckbox,
  handleUpdateDataCheckbox,
} from "../../../Utils/helper";
import {
  getApi,
  postApi,
  putApi,
  deleteApi,
} from "../../../Services/apiService";
import { toast } from "react-toastify";
import Import from "../../../Utils/import";
import { TableFooter } from "../../../Utils/helper";
import DeleteModal from "../../../Utils/deleteModal";

function Origin() {
  const [methodType, setMethodType] = useState("");
  const { data } = useSelector((state) => state.roleData);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [productOrigin, setProductOrigin] = useState([]);
  const [newOriginName, setNewOriginName] = useState("");
  const [newOriginCode, setNewOriginCode] = useState("");
  const [editingProductOrigin, setEditingProductOrigin] = useState(null);
  const [editOriginName, setEditOriginName] = useState("");
  const [editOriginCode, setEditOriginCode] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [originToDelete, setOriginToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [pageCount, setPageCount] = useState(1);
  const [validationErrors, setValidationErrors] = useState({
    newOriginName: "",
    newOriginCode: "",
    editOriginName: "",
    editOriginCode: "",
  });
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importErrors, setImportErrors] = useState({
    file: "",
    general: "",
  });

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1)
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchProductOriginData();
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery, currentPage, itemsPerPage]);

  const fetchProductOriginData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const resp = await getApi(
        `/product-origin?search=${encodeURIComponent(
          searchQuery
        )}&page=${currentPage}&per_page=${itemsPerPage}`
      );

      if (resp?.success === true) {
        setProductOrigin(resp?.data?.productOrigins?.data || []);
        setPageCount(resp?.data?.productOrigins?.last_page || 1);
      } else {
        setError(resp?.msg || "Failed to fetch origin details");
        toast.error(resp?.msg || "Failed to fetch origin details");
      }
    } catch (error) {
      setError(error.message || "Failed to fetch origin details");
      toast.error("Failed to fetch origin details");
    } finally {
      setIsLoading(false);
    }
  };

  const validateForm = (isEdit = false) => {
    const errors = {
      newOriginName: "",
      newOriginCode: "",
      editOriginName: "",
      editOriginCode: "",
    };
    let isValid = true;

    if (isEdit) {
      if (!editOriginName.trim()) {
        errors.editOriginName = "Origin name is required";
        isValid = false;
      }
      if (!editOriginCode.trim()) {
        errors.editOriginCode = "Origin code is required";
        isValid = false;
      } else {
        const code = parseInt(editOriginCode);
        if (isNaN(code) || code < 0 || code > 99) {
          errors.editOriginCode = "Origin code must be between 0 and 99";
          isValid = false;
        }
      }
    } else {
      if (!newOriginName.trim()) {
        errors.newOriginName = "Origin name is required";
        isValid = false;
      }
      if (!newOriginCode.trim()) {
        errors.newOriginCode = "Origin code is required";
        isValid = false;
      } else {
        const code = parseInt(newOriginCode);
        if (isNaN(code) || code < 0 || code > 99) {
          errors.newOriginCode = "Origin code must be between 0 and 99";
          isValid = false;
        }
      }
    }

    setValidationErrors(errors);
    return isValid;
  };

  const handlePageClick = (selected) => {
    setCurrentPage(selected);
  };

  const handleAddOrigin = async () => {
    if (!validateForm(false)) return;

    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append("name", newOriginName);
      formData.append("code", newOriginCode);
      const response = await postApi("/product-origin", formData);

      if (response?.success) {
        toast.success(response.msg || "Product origin added successfully");
        setNewOriginName("");
        setNewOriginCode("");
        setMethodType("");
        setValidationErrors({
          newOriginName: "",
          newOriginCode: "",
          editOriginName: "",
          editOriginCode: "",
        });
        fetchProductOriginData();
      } else {
        toast.error(response?.msg || "Failed to add product origin");
      }
    } catch (error) {
      const errorMsg =
        error.response?.data?.msg ||
        error.message ||
        "Failed to add product origin";
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditOrigin = async () => {
    if (!validateForm(true)) return;

    try {
      setIsLoading(true);
      const payload = {
        name: editOriginName,
        code: editOriginCode,
      };
      const response = await putApi(
        `/product-origin/${editingProductOrigin.id}`,
        payload
      );

      if (response?.success) {
        toast.success(response.msg || "Product origin updated successfully");
        setEditOriginName("");
        setEditOriginCode("");
        setEditingProductOrigin(null);
        setMethodType("");
        setValidationErrors({
          newOriginName: "",
          newOriginCode: "",
          editOriginName: "",
          editOriginCode: "",
        });
        fetchProductOriginData();
      } else {
        toast.error(response?.msg || "Failed to update product origin");
      }
    } catch (error) {
      const errorMsg =
        error.response?.data?.msg ||
        error.message ||
        "Failed to update product origin";
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const confirmDeleteOrigin = async () => {
    if (!originToDelete) return;

    try {
      setIsLoading(true);
      const response = await deleteApi(`/product-origin/${originToDelete.id}`);

      if (response?.success) {
        toast.success(response.msg || "Product origin deleted successfully");
        setShowDeleteModal(false);
        setOriginToDelete(null);
        fetchProductOriginData();
      } else {
        toast.error(response?.msg || "Failed to delete product origin");
      }
    } catch (error) {
      const errorMsg =
        error.response?.data?.msg ||
        error.message ||
        "Failed to delete product origin";
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const openEditForm = (method) => {
    setEditingProductOrigin(method);
    setEditOriginName(method.name);
    setEditOriginCode(method.code);
    setMethodType("edit");
    setValidationErrors({
      newOriginName: "",
      newOriginCode: "",
      editOriginName: "",
      editOriginCode: "",
    });
  };

  const handleInputBlur = (field, isEdit = false) => {
    if (isEdit) {
      if (field === "name" && !editOriginName.trim()) {
        setValidationErrors((prev) => ({
          ...prev,
          editOriginName: "Origin name is required",
        }));
      } else if (field === "code") {
        if (!editOriginCode.trim()) {
          setValidationErrors((prev) => ({
            ...prev,
            editOriginCode: "Origin code is required",
          }));
        } else {
          const code = parseInt(editOriginCode);
          if (isNaN(code) || code < 0 || code > 99) {
            setValidationErrors((prev) => ({
              ...prev,
              editOriginCode: "Origin code must be between 0 and 99",
            }));
          }
        }
      }
    } else {
      if (field === "name" && !newOriginName.trim()) {
        setValidationErrors((prev) => ({
          ...prev,
          newOriginName: "Origin name is required",
        }));
      } else if (field === "code") {
        if (!newOriginCode.trim()) {
          setValidationErrors((prev) => ({
            ...prev,
            newOriginCode: "Origin code is required",
          }));
        } else {
          const code = parseInt(newOriginCode);
          if (isNaN(code) || code < 0 || code > 99) {
            setValidationErrors((prev) => ({
              ...prev,
              newOriginCode: "Origin code must be between 0 and 99",
            }));
          }
        }
      }
    }
  };

  return (
    <div className="RightSideDiv product-single category">
      {showImportModal && (
        <Import
          setImportFile={setImportFile}
          importFile={importFile}
          searchQuery={searchQuery}
          fetchData={fetchProductOriginData}
          setShowImportModal={setShowImportModal}
          routeEnd={"/product-origin/import"}
          routeexport={"/product-origin/export"}
          type={'Origin'}
        />
      )}
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <DeleteModal
          setShowDeleteModal={setShowDeleteModal}
          todelete={originToDelete}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          settoDelete={setOriginToDelete}
          fetchData={fetchProductOriginData}
          route={`/product-origin/${originToDelete?.id}`}
          type={"origin"}
        />
      )}

      {/* Add Origin Form */}
      {methodType === "add" && (
        <div className="row">
          <div className="col-sm-12">
            <div className="setting-right position-relative">
              <h3>Add Origin</h3>
              <img
                src={window.getPublicUrl("/assets/images/close.svg")}
                className="close"
                alt=""
                onClick={() => {
                  setMethodType("");
                  setNewOriginName("");
                  setNewOriginCode("");
                  setValidationErrors({
                    newOriginName: "",
                    newOriginCode: "",
                    editOriginName: "",
                    editOriginCode: "",
                  });
                }}
              />
              <div className="setting-inner">
                <div className="row">
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label>
                        Origin<strong>*</strong>
                      </label>
                      <input
                        type="text"
                        className={`form-control ${validationErrors.newOriginName ? "is-invalid" : ""
                          }`}
                        placeholder="Enter Origin"
                        value={newOriginName}
                        onChange={(e) => {
                          setNewOriginName(e.target.value);
                          if (e.target.value.trim()) {
                            setValidationErrors((prev) => ({
                              ...prev,
                              newOriginName: "",
                            }));
                          }
                        }}
                        onBlur={() => handleInputBlur("name", false)}
                      />
                      {validationErrors.newOriginName && (
                        <div className="invalid-feedback">
                          {validationErrors.newOriginName}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label>
                        Code<strong>*</strong>
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="99"
                        className={`form-control ${validationErrors.newOriginCode ? "is-invalid" : ""}`}
                        placeholder="Enter Code (0-99)"
                        value={newOriginCode}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Only allow numbers and empty string
                          if (value === "" || /^[0-9\b]+$/.test(value)) {
                            setNewOriginCode(value);
                            if (value.trim()) {
                              setValidationErrors((prev) => ({
                                ...prev,
                                newOriginCode: "",
                              }));
                            }
                          }
                        }}
                        onBlur={() => handleInputBlur("code", false)}
                      />
                      {validationErrors.newOriginCode && (
                        <div className="invalid-feedback">
                          {validationErrors.newOriginCode}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="button-settings">
                <div className="col-sm-12">
                  <div className="d-flex justify-content-end gap-3">
                    <button
                      className="reset"
                      onClick={() => {
                        setNewOriginName("");
                        setNewOriginCode("");
                        setValidationErrors({
                          newOriginName: "",
                          newOriginCode: "",
                          editOriginName: "",
                          editOriginCode: "",
                        });
                      }}
                    >
                      Reset
                    </button>
                    <button
                      className="save"
                      onClick={handleAddOrigin}
                      disabled={isLoading}
                    >
                      {isLoading ? "Saving..." : "Save"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Origin Form */}
      {methodType === "edit" && (
        <div className="row">
          <div className="col-sm-12">
            <div className="setting-right position-relative">
              <h3>Edit Origin</h3>
              <img
                src={window.getPublicUrl("/assets/images/close.svg")}
                className="close"
                alt=""
                onClick={() => {
                  setMethodType("");
                  setEditOriginName("");
                  setEditOriginCode("");
                  setEditingProductOrigin(null);
                  setValidationErrors({
                    newOriginName: "",
                    newOriginCode: "",
                    editOriginName: "",
                    editOriginCode: "",
                  });
                }}
              />
              <div className="setting-inner">
                <div className="row">
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label>
                        Origin<strong>*</strong>
                      </label>
                      <input
                        type="text"
                        className={`form-control ${validationErrors.editOriginName ? "is-invalid" : ""
                          }`}
                        placeholder="Enter Origin"
                        value={editOriginName}
                        onChange={(e) => {
                          setEditOriginName(e.target.value);
                          if (e.target.value.trim()) {
                            setValidationErrors((prev) => ({
                              ...prev,
                              editOriginName: "",
                            }));
                          }
                        }}
                        onBlur={() => handleInputBlur("name", true)}
                      />
                      {validationErrors.editOriginName && (
                        <div className="invalid-feedback">
                          {validationErrors.editOriginName}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label>
                        Code<strong>*</strong>
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="99"
                        className={`form-control ${validationErrors.editOriginCode ? "is-invalid" : ""}`}
                        placeholder="Enter Code (0-99)"
                        value={editOriginCode}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Only allow numbers and empty string
                          if (value === "" || /^[0-9\b]+$/.test(value)) {
                            setEditOriginCode(value);
                            if (value.trim()) {
                              setValidationErrors((prev) => ({
                                ...prev,
                                editOriginCode: "",
                              }));
                            }
                          }
                        }}
                        onBlur={() => handleInputBlur("code", true)}
                      />
                      {validationErrors.editOriginCode && (
                        <div className="invalid-feedback">
                          {validationErrors.editOriginCode}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="button-settings">
                <div className="col-sm-12">
                  <div className="d-flex justify-content-end gap-3">
                    <button
                      className="reset"
                      onClick={() => {
                        setEditOriginName(editingProductOrigin.name);
                        setEditOriginCode(editingProductOrigin.code);
                        setValidationErrors((prev) => ({
                          ...prev,
                          editOriginName: "",
                          editOriginCode: "",
                        }));
                      }}
                    >
                      Reset
                    </button>
                    <button
                      className="save"
                      onClick={handleEditOrigin}
                      disabled={isLoading}
                    >
                      {isLoading ? "Saving..." : "Save"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Table View */}
      {methodType === "" && (
        <div className="row">
          <div className="col-sm-12">
            <div className="setting-right setting-right-table">
              <div className="table-header">
                <div className="search-card">
                  <img
                    src={window.getPublicUrl("/assets/images/search-icon.svg")}
                    alt=""
                  />
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Search Product origin..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                  />
                </div>
                <div className="table-actions">
                {checkAddPermission(data?.permissions, "origin") && (
                  <button
                    className="btn"
                    onClick={() =>
                      handleImportClickFile(
                        setShowImportModal,
                        setImportFile,
                        setImportErrors
                      )
                    }
                  >
                    <img
                      src={window.getPublicUrl("/assets/images/import.png")}
                      alt=""
                    />
                    Import
                  </button>
                )}
                  <button
                    className="btn"
                    onClick={() =>
                      handleExportFile(
                        productOrigin,
                        searchQuery,
                        "productOrigin.xlsx",
                        "/product-origin/export"
                      )
                    }
                  >
                    <img
                      src={window.getPublicUrl("/assets/images/export.svg")}
                      alt=""
                    />
                    Export
                  </button>
                  {checkAddPermission(data?.permissions, "origin") && (
                    <button
                      className="btn add"
                      onClick={() => setMethodType("add")}
                    >
                      + Add New Origin
                    </button>
                  )}
                </div>
              </div>

              <div className="permission-table-inner category-table">
                <table className="custom-table table-striped">
                  <thead>
                    <tr>
                      <th className="checkbox">
                        <label className="checbox-cs">
                          <input
                            type="checkbox"
                            onClick={(e) =>
                              handleUpdateAllCheckbox(
                                e?.target?.checked,
                                setProductOrigin
                              )
                            }
                          />
                          <span className="checkmark"></span>
                        </label>
                      </th>
                      <th className="code">Code</th>
                      <th className="name">Name</th>
                      <th className="action">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      <tr>
                        <td colSpan="4" className="text-center">
                          <div
                            className="spinner-border spinner-border-sm"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        </td>
                      </tr>
                    ) : error ? (
                      <tr>
                        <td colSpan="4" className="text-center text-danger">
                          {error}
                        </td>
                      </tr>
                    ) : productOrigin.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="text-center">
                          No origin found
                        </td>
                      </tr>
                    ) : (
                      productOrigin.map((method) => (
                        <tr key={method.id}>
                          <td className="checkbox">
                            <label className="checbox-cs">
                              <input
                                type="checkbox"
                                onClick={(e) =>
                                  handleUpdateDataCheckbox(
                                    method,
                                    e,
                                    setProductOrigin
                                  )
                                }
                                checked={method?.status}
                              />
                              <span className="checkmark"></span>
                            </label>
                          </td>
                          <td className="code">{method.code}</td>
                          <td className="name">{method.name}</td>
                          <td className="action action-cs">
                            <div className="dropdown">
                              {/* <button class=" dropdown-toggle action-btn" type="button" id="dropdownMenuButton1" data-bs-toggle="dropdown" aria-expanded="false">
                                Action <img src={window.getPublicUrl('/assets/images/action-drops.png')} alt="Actions" className="ps-2" />
                              </button> */}
                                {(checkEditPermission(data?.permissions, 'origin') || checkDeletePermission(data?.permissions, 'origin')) && (
                              <button class="btn btn-secondary dropdown-toggle action-btn" type="button" id="dropdownMenuButton1" data-bs-toggle="dropdown" aria-expanded="false"> <img src={window.getPublicUrl('/assets/images/dots-icon.svg')} alt="" /></button>
                                )}
                              <ul class="dropdown-menu" aria-labelledby="dropdownMenuButton1">
                                <li>
                                  {checkEditPermission(
                                    data?.permissions,
                                    "origin"
                                  ) && (
                                      <a href="#" className="dropdown-item" onClick={() => openEditForm(method)}>Edit</a>
                                    )}
                                </li>
                                <li>
                                  {checkDeletePermission(
                                    data?.permissions,
                                    "origin"
                                  ) && (

                                      <a href="#" className="dropdown-item" onClick={() => {
                                        setOriginToDelete(method);
                                        setShowDeleteModal(true);
                                      }}>Delete</a>
                                    )}
                                </li>
                              </ul>



                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>

             
              </div>
              <div className="table-footer d-flex justify-content-between table-pagination">
                  <select
                    name=""
                    id=""
                    className="affter-change-cs page"
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1); // Reset to first page when changing items per page
                    }}
                  >
                    <option value="50">50</option>
                    <option value="100">100</option>
                    <option value="250">250</option>
                    <option value="500">500</option>
                  </select>
                  <div>
                    <TableFooter
                      pageCount={pageCount}
                      handlePageClick={handlePageClick}
                      currentPage={currentPage}
                    />
                  </div>
                </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Origin;
